import React from "react";
import { cn } from "../../../lib/utils";
import { formatTimestamp, formatDuration, eventTypeLabel } from "../utils/gatewayUtils";
import GatewayStatusBadge from "./GatewayStatusBadge";
import GatewayModuleBadge from "./GatewayModuleBadge";

interface TimelineEvent {
  id?: string;
  timestamp: string;
  eventType?: string;
  module?: string;
  status: string;
  durationMs?: number;
  duration?: number; // legacy
  capabilityName?: string;
  serverName?: string;
  serverConfigName?: string; // legacy
  correlationId?: string;
  eventSequence?: number;
  errorMessage?: string;
  pdpDecision?: string;
  pdpPolicyId?: string;
  pdpReason?: string;
  responsePayload?: any;
}

// Trace-only per-event subtitle: scope/TTL/audience (mint), the PDP decision + deciding
// policy + reason, capability and server — mirrors the OG trace chain's event detail line.
function EventDetail({ event }: { event: TimelineEvent }) {
  const p = event.responsePayload || {};
  const bits: React.ReactNode[] = [];
  const code = (v: React.ReactNode) => <code className="text-primary font-mono">{v}</code>;
  if (event.eventType === "STS_TOKEN_MINTED") {
    if (p.scope) bits.push(<>Scope {code(p.scope)}</>);
    if (p.ttlSeconds) bits.push(<>TTL {p.ttlSeconds}s</>);
    if (p.aud) bits.push(<>Audience {code(p.aud)}</>);
  }
  if (event.pdpDecision) bits.push(<>Decision <strong className="text-foreground/90">{event.pdpDecision}</strong></>);
  if (event.pdpPolicyId) bits.push(<>Decided by {code(event.pdpPolicyId)}</>);
  if (event.pdpReason) bits.push(<>{event.pdpReason}</>);
  if (event.capabilityName && event.eventType !== "STS_TOKEN_MINTED") bits.push(<>{event.capabilityName}</>);
  if (event.serverName) bits.push(<>Server {event.serverName}</>);
  if (!bits.length) return null;
  return (
    <div className="text-[11px] text-muted-foreground mt-0.5 break-words">
      {bits.map((b, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1 text-muted-foreground/50">·</span>}
          {b}
        </React.Fragment>
      ))}
    </div>
  );
}

interface GatewayTimelineProps {
  events: TimelineEvent[];
  className?: string;
  // Humanize event-type labels (used by the Request Trace modal; correlation/session stay raw)
  humanizeEvents?: boolean;
}

// Smaller, denser badges for the timeline (overrides the base badge sizing via twMerge).
const TIMELINE_BADGE = "text-[9px] px-1 py-0 leading-[14px]";

function dotColor(status: string): string {
  if (status === "SUCCESS" || status === "COMPLETED") return "bg-emerald-500";
  if (status === "ERROR" || status === "FAILURE") return "bg-red-500";
  if (status === "DENIED") return "bg-orange-500";
  return "bg-primary";
}

/**
 * ONE grid for the whole timeline (not a grid per row). With a single grid the
 * status / module / duration / timestamp columns are sized to the widest content
 * across ALL rows, so every badge lines up in a clean column — and nothing can
 * overflow. The rail (dot + connector) is the first column; the name column is
 * minmax(0,1fr) so it absorbs slack when wide and shrinks/wraps when narrow.
 */
export default function GatewayTimeline({ events, className, humanizeEvents }: GatewayTimelineProps) {
  if (!events || events.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-4">No events</div>;
  }

  return (
    <div
      className={cn("grid items-start gap-x-3", className)}
      style={{ gridTemplateColumns: "16px minmax(0,1fr) auto auto auto auto" }}
    >
      {events.map((event, i) => {
        const isLast = i === events.length - 1;
        const dur = event.durationMs ?? event.duration;
        return (
          <React.Fragment key={event.id || i}>
            {/* rail — stretches to the row height so the connector reaches the next dot */}
            <div className="flex flex-col items-center self-stretch">
              <div className={cn("w-2.5 h-2.5 rounded-full mt-1 shrink-0 z-10", dotColor(event.status))} />
              {!isLast && <div className="w-[2px] flex-1 bg-muted-foreground/25 min-h-[16px]" />}
            </div>

            {/* name + correlation id + error */}
            <div className="min-w-0 break-words pb-4">
              <span className="text-xs text-foreground/80 break-words">
                {humanizeEvents ? eventTypeLabel(event.eventType) : event.eventType || "Event"}
              </span>
              {event.correlationId && (
                <span className="text-[10px] text-muted-foreground/50 font-mono ml-2 whitespace-nowrap">
                  ID: {event.correlationId.slice(0, 8)}
                </span>
              )}
              {humanizeEvents && <EventDetail event={event} />}
              {event.errorMessage && (
                <div className="text-[11px] text-red-600/90 dark:text-red-400/90 mt-1 font-mono break-all whitespace-pre-wrap bg-red-500/5 border border-red-500/15 rounded px-2 py-1.5">
                  {event.errorMessage}
                </div>
              )}
            </div>

            {/* module (sits next to the event name; status follows it) */}
            <div className="flex justify-end pb-4 whitespace-nowrap">
              {event.module ? <GatewayModuleBadge module={event.module} className={TIMELINE_BADGE} /> : null}
            </div>

            {/* status */}
            <div className="flex justify-end pb-4">
              <GatewayStatusBadge status={event.status} className={TIMELINE_BADGE} />
            </div>

            {/* duration */}
            <div className="text-[10px] text-muted-foreground font-mono text-right pb-4 whitespace-nowrap">
              {dur != null ? formatDuration(dur) : ""}
            </div>

            {/* timestamp */}
            <div className="text-[10px] text-muted-foreground/60 font-mono text-right pb-4 whitespace-nowrap">
              {formatTimestamp(event.timestamp)}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
